import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, Mark, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Scissors, ArrowUpToLine, Highlighter } from 'lucide-react';

// One flowing editor for a whole chapter's voice-over.
//
// The table view edits a slide at a time, which is right for structure and wrong for
// writing: adding a sentence or moving one across a slide boundary should be ordinary
// typing, not cutting and pasting between fields.
//
// The schema is deliberately narrow: paragraphs, a SlideBoundary node and one Reveal
// mark. No headings, bold, italic, alignment, tables or inline images, because none of
// them mean anything to a person reading a script aloud, and allowing them would put
// markup back into a field whose whole value is that it is plain text.

// Boundaries as NODES are what make split and merge fall out: split inserts one at the
// cursor, merge deletes one. Nothing has to track offsets.
const SlideBoundary = Node.create({
  name: 'slideBoundary',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      sid: { default: null },
      slideNo: { default: '' },
      videoTitle: { default: '' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-slide-boundary]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-slide-boundary': 'true' })];
  },
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'my-3 flex items-center gap-2 select-none';
      dom.setAttribute('data-slide-boundary', 'true');
      const line = document.createElement('div');
      line.className = 'flex-1 border-t border-dashed border-[#2b3a4d]';
      const label = document.createElement('span');
      label.className = 'text-[10px] uppercase tracking-wide text-[#5f6b7e] whitespace-nowrap';
      // The video title is shown but not editable here: the VIDEO column is the table
      // view's job, so there is one place that owns it.
      label.textContent = node.attrs.videoTitle
        ? `slide ${node.attrs.slideNo} · ${node.attrs.videoTitle}`
        : `slide ${node.attrs.slideNo}`;
      const line2 = document.createElement('div');
      line2.className = 'flex-1 border-t border-dashed border-[#2b3a4d]';
      dom.append(line, label, line2);
      return { dom };
    };
  },
});

// A local mark, not @tiptap/extension-highlight: that package is not a dependency and
// its default is yellow, which the palette forbids. ZU's template reserves highlight
// for where an answer should be revealed, so that is the only thing it means here.
const Reveal = Mark.create({
  name: 'reveal',
  parseHTML() {
    return [{ tag: 'mark[data-reveal]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['mark', mergeAttributes(HTMLAttributes, {
      'data-reveal': 'true',
      class: 'bg-emerald-500/40 text-[#e6edf7] rounded px-0.5',
    }), 0];
  },
  addCommands() {
    return {
      toggleReveal: () => ({ commands }) => commands.toggleMark(this.name),
    };
  },
});

// StarterKit configured DOWN rather than four separate packages added: it already
// bundles document, paragraph, text and history, and everything switched off here is
// formatting that means nothing to spoken narration.
const EXTENSIONS = [
  StarterKit.configure({
    heading: false,
    bold: false,
    italic: false,
    strike: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    horizontalRule: false,
    hardBreak: false,
    dropcursor: false,
  }),
  SlideBoundary,
  Reveal,
];

// Chapter slides to a ProseMirror document. Each slide's narration becomes paragraphs,
// preceded by a boundary node for every slide after the first.
function toDoc(slides, numbering) {
  const content = [];
  slides.forEach((slide, index) => {
    if (index > 0) {
      content.push({
        type: 'slideBoundary',
        attrs: {
          sid: slide.sid,
          slideNo: numbering?.[slide.sid] || '',
          videoTitle: slide.video_title || '',
        },
      });
    }
    const text = slide.audio || '';
    const marks = (slide.reveal_marks || []).slice().sort((a, b) => a.start - b.start);
    const nodes = [];
    let cursor = 0;
    marks.forEach((m) => {
      const start = Math.max(0, Math.min(m.start, text.length));
      const end = Math.max(start, Math.min(start + m.length, text.length));
      if (start > cursor) nodes.push({ type: 'text', text: text.slice(cursor, start) });
      if (end > start) {
        nodes.push({ type: 'text', text: text.slice(start, end), marks: [{ type: 'reveal' }] });
      }
      cursor = end;
    });
    if (cursor < text.length) nodes.push({ type: 'text', text: text.slice(cursor) });
    content.push({ type: 'paragraph', content: nodes.length ? nodes : undefined });
  });
  if (!content.length) content.push({ type: 'paragraph' });
  return { type: 'doc', content };
}

// ProseMirror document back to per-slide narration and reveal offsets.
//
// Offsets are recomputed from the editor on every save, which is why the document can
// store plain text plus offsets rather than markup: nothing downstream has to parse a
// tag to count a word.
export function fromDoc(json, slides) {
  const groups = [[]];
  const boundarySids = [];
  (json?.content || []).forEach((node) => {
    if (node.type === 'slideBoundary') {
      boundarySids.push(node.attrs?.sid || null);
      groups.push([]);
      return;
    }
    groups[groups.length - 1].push(node);
  });

  return groups.map((nodes, index) => {
    let text = '';
    const marks = [];
    nodes.forEach((para, pi) => {
      if (pi > 0) text += '\n';
      (para.content || []).forEach((run) => {
        const value = run.text || '';
        if ((run.marks || []).some((m) => m.type === 'reveal') && value.length) {
          marks.push({ start: text.length, length: value.length });
        }
        text += value;
      });
    });
    // A slide the author did not split away keeps its identity, so its images and its
    // VIDEO column survive an edit to the narration.
    const existing = index === 0 ? slides[0] : slides.find((s) => s.sid === boundarySids[index - 1]);
    return {
      sid: existing?.sid || null,
      audio: text,
      reveal_marks: marks,
    };
  });
}

export default function AvScriptNarration({ chapter, numbering, onChange, onSplit, onMergeUp }) {
  const slides = useMemo(() => chapter.slides || [], [chapter.slides]);
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: toDoc(slides, numbering),
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none text-sm leading-relaxed focus:outline-none min-h-[8rem]',
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(fromDoc(ed.getJSON(), slides)),
  }, [chapter.cid]);

  // Reload when the document changes underneath (a regeneration landing, a 409 reload).
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(toDoc(slides, numbering), false);
  }, [editor, chapter.content_rev]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;

  // Which slide the cursor is in, and how far into that slide's narration.
  //
  // Both numbers must agree with fromDoc's serialization, which joins a slide's paragraphs
  // with a newline. Counting only text characters made a split land one character early
  // for every paragraph above the cursor.
  const cursorPosition = () => {
    const pos = editor.state.selection.from;
    let index = 0;
    let offset = 0;
    let paragraphsInSlide = 0;
    let done = false;

    editor.state.doc.forEach((node, nodePos) => {
      if (done) return;
      if (node.type.name === 'slideBoundary') {
        // A selection ON a boundary belongs to the slide BELOW it, which is the one a
        // "merge with previous" is about. Not counting it merged the slide above instead.
        if (nodePos >= pos) { done = true; return; }
        index += 1;
        offset = 0;
        paragraphsInSlide = 0;
        return;
      }
      if (nodePos >= pos) { done = true; return; }
      if (paragraphsInSlide > 0) offset += 1; // the newline fromDoc will insert
      const inner = node.textContent.length;
      // A paragraph occupies nodePos+1 .. nodePos+1+inner.
      offset += pos >= nodePos + node.nodeSize ? inner
        : Math.max(0, Math.min(inner, pos - nodePos - 1));
      paragraphsInSlide += 1;
    });
    return { index, offset };
  };

  // Identity, not position, is what the server is told: a concurrent change must not be
  // able to redirect a split or a merge onto a different slide.
  const sidAtCursor = () => {
    const { index } = cursorPosition();
    return slides[index]?.sid || null;
  };

  const btn = 'inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#2b3a4d] text-[#8a95a8] hover:bg-[#12202f]';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" className={btn}
          onClick={() => editor.chain().focus().toggleReveal().run()}
          title="Mark where the answer should be revealed. ZU reserves highlighting for this.">
          <Highlighter size={12} /> Reveal point
        </button>
        <button type="button" className={btn}
          onClick={() => {
            const { offset } = cursorPosition();
            onSplit?.({ sid: sidAtCursor(), atChar: offset });
          }}
          title="Split into a new slide at the cursor">
          <Scissors size={12} /> Split here
        </button>
        <button type="button" className={btn}
          onClick={() => onMergeUp?.({ sid: sidAtCursor() })}
          title="Merge this slide into the one above it">
          <ArrowUpToLine size={12} /> Merge with previous
        </button>
        <span className="text-[10px] text-[#5f6b7e]">
          Voice-over only. Slide titles and on-screen lines are edited in the table view.
        </span>
      </div>
      <div className="border border-[#2b3a4d] rounded-lg bg-[#0b0e13] p-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
